import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp, collectionGroup, query, orderBy, limit, getDoc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { Toast } from '../components/InAppNotification';
import { SeverityTrendTremor, WcagCriteriaBreakdownNivo, ImpactPieChartNivo } from '../components/AnalyticsCharts';
import { getGradeFromRisk } from '../lib/parseResults';



export default function AdminDashboardPage({ onViewChange }) {
    const { user, profile } = useAuth();
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [stats, setStats] = useState({ totalUsers: 0, adminCount: 0 });
    const [recentScans, setRecentScans] = useState([]);
    const [analytics, setAnalytics] = useState({ totalScansGlobally: 0, extensionScans: 0, agentScans: 0, totalViolationsFound: 0 });

    const [activeTab, setActiveTab] = useState('users'); // 'users' | 'analytics' | 'settings'
    const [dateFilter, setDateFilter] = useState('all'); // 'all', '24h', '7d', '30d'
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' | 'asc'
    const [agentCountRefreshMinutes, setAgentCountRefreshMinutes] = useState(5); // Default 5 minutes
    const [chromeStoreLink, setChromeStoreLink] = useState('');
    const [edgeStoreLink, setEdgeStoreLink] = useState('');

    const [managingUser, setManagingUser] = useState(null); // UID of user being edited
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // UID of user to delete
    
    // Plans State
    const [plans, setPlans] = useState([]);
    const [managingPlan, setManagingPlan] = useState(null);
    const [showPlanDeleteConfirm, setShowPlanDeleteConfirm] = useState(null);

    const [editData, setEditData] = useState({ displayName: '', company: '', jobTitle: '' });
    const [actionLoading, setActionLoading] = useState(false);

    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSortKey, setUserSortKey] = useState('name'); // 'name' | 'company' | 'role' | 'date'
    const [userSortOrder, setUserSortOrder] = useState('asc'); // 'asc' | 'desc'
    const [adminNotifications, setAdminNotifications] = useState([]);
    const [adminActivity, setAdminActivity] = useState([]);
    const [broadcast, setBroadcast] = useState({ title: '', message: '', severity: 'info' });


    useEffect(() => {
        const fetchGlobalData = async () => {
            if (!user || profile?.role !== 'admin') {
                setLoading(false);
                return;
            }

            try {
                // Fetch all users
                const usersRef = collection(db, 'users');
                const usersSnap = await getDocs(usersRef);

                const usersData = usersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                const totalAdmins = usersData.filter(u => u.role === 'admin').length;

                setUsersList(usersData);
                setStats({
                    totalUsers: usersData.length,
                    adminCount: totalAdmins
                });

                // Fetch Global Scans (limit 5000 for browser performance)
                const scansQuery = query(
                    collectionGroup(db, 'queue'),
                    orderBy('createdAt', 'desc'),
                    limit(5000)
                );
                const scansSnap = await getDocs(scansQuery);
                const scansData = scansSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setRecentScans(scansData);

                let extScans = 0;
                let agtScans = 0;
                let totalVio = 0;

                scansData.forEach(scan => {
                    // Unified scan source/type detection
                    const isExtension = scan.isExtension || scan.source === 'companion-extension';

                    if (isExtension) extScans++;
                    else agtScans++;

                    if (scan.summary && scan.summary.totalViolations) {
                        totalVio += scan.summary.totalViolations;
                    }
                });

                setAnalytics({
                    totalScansGlobally: scansData.length,
                    extensionScans: extScans,
                    agentScans: agtScans,
                    totalViolationsFound: totalVio
                });

                // Load public config for agent count refresh interval
                try {
                    const configSnap = await getDoc(doc(db, 'config', 'public'));
                    if (configSnap.exists()) {
                        const configData = configSnap.data();
                        setAgentCountRefreshMinutes(Math.round((configData.agentCountRefreshMs || 300000) / 60000));
                        setChromeStoreLink(configData.chromeStoreLink || '');
                        setEdgeStoreLink(configData.edgeStoreLink || '');
                    }
                } catch (configErr) {
                    console.warn('Failed to load config:', configErr.message);
                }

                // Fetch Plans
                try {
                    const plansRef = collection(db, 'plans');
                    const plansSnap = await getDocs(plansRef);
                    setPlans(plansSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b) => (a.tier || 0) - (b.tier || 0)));
                } catch (plansErr) {
                    console.warn('Failed to load plans:', plansErr.message);
                }

            } catch (err) {
                console.error("Error fetching global admin data:", err);
                setToast({ message: 'Failed to load global data. Are you an admin?', type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchGlobalData();
    }, [user, profile]);

    useEffect(() => {
        if (profile?.role !== 'admin') return undefined;
        const notificationQuery = query(collection(db, 'adminNotifications'), orderBy('createdAt', 'desc'), limit(50));
        const activityQuery = query(collection(db, 'adminActivity'), orderBy('createdAt', 'desc'), limit(100));
        const stopNotifications = onSnapshot(notificationQuery, snap => setAdminNotifications(snap.docs.map(item => ({ id: item.id, ...item.data() }))));
        const stopActivity = onSnapshot(activityQuery, snap => setAdminActivity(snap.docs.map(item => ({ id: item.id, ...item.data() }))));
        return () => { stopNotifications(); stopActivity(); };
    }, [profile?.role]);

    const publishBroadcast = async (event) => {
        event.preventDefault();
        if (!broadcast.title.trim() || !broadcast.message.trim()) return;
        try {
            await httpsCallable(functions, 'publishAnnouncement')({
                title: broadcast.title.trim(),
                message: broadcast.message.trim(),
                severity: broadcast.severity
            });
            setBroadcast({ title: '', message: '', severity: 'info' });
            setToast({ message: 'Broadcast published to all users.', type: 'success' });
        } catch (error) {
            console.error('Failed to publish broadcast:', error);
            setToast({ message: 'Failed to publish broadcast.', type: 'error' });
        }
    };

    const handleSavePublicConfig = async () => {
        setActionLoading(true);
        try {
            await setDoc(doc(db, 'config', 'public'), {
                agentCountRefreshMs: agentCountRefreshMinutes * 60000,
                chromeStoreLink: chromeStoreLink,
                edgeStoreLink: edgeStoreLink,
                updatedAt: serverTimestamp(),
                updatedBy: user.uid
            }, { merge: true });
            setToast({ message: 'Public settings saved successfully.', type: 'success' });
        } catch (err) {
            console.error('Failed to save config:', err);
            setToast({ message: 'Failed to save settings.', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleSavePlan = async () => {
        setActionLoading(true);
        try {
            const planData = { ...managingPlan, updatedAt: serverTimestamp() };
            let docRefId = managingPlan.id;
            
            if (docRefId) {
                delete planData.id;
                await updateDoc(doc(db, 'plans', docRefId), planData);
                setPlans(prev => prev.map(p => p.id === docRefId ? { ...planData, id: docRefId } : p).sort((a,b) => (a.tier || 0) - (b.tier || 0)));
            } else {
                planData.createdAt = serverTimestamp();
                const newDocRef = doc(collection(db, 'plans'));
                await setDoc(newDocRef, planData);
                docRefId = newDocRef.id;
                setPlans(prev => [...prev, { ...planData, id: docRefId }].sort((a,b) => (a.tier || 0) - (b.tier || 0)));
            }
            
            setManagingPlan(null);
            setToast({ message: 'Plan saved successfully.', type: 'success' });
        } catch (err) {
            console.error('Failed to save plan:', err);
            setToast({ message: 'Failed to save plan.', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeletePlan = async (planId) => {
        setActionLoading(true);
        try {
            await deleteDoc(doc(db, 'plans', planId));
            setPlans(prev => prev.filter(p => p.id !== planId));
            setShowPlanDeleteConfirm(null);
            setToast({ message: 'Plan deleted.', type: 'success' });
        } catch (err) {
            console.error('Failed to delete plan:', err);
            setToast({ message: 'Failed to delete plan.', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRoleChange = async (targetUid, currentRole) => {
        try {
            const newRole = currentRole === 'admin' ? 'user' : 'admin';
            await updateDoc(doc(db, 'users', targetUid), {
                role: newRole,
                updatedAt: serverTimestamp()
            });

            // Optimistic UI update
            setUsersList(prev => prev.map(u => u.id === targetUid ? { ...u, role: newRole } : u));
            setStats(prev => ({
                ...prev,
                adminCount: newRole === 'admin' ? prev.adminCount + 1 : prev.adminCount - 1
            }));

            setToast({ message: `User role updated to ${newRole}`, type: 'success' });
        } catch (err) {
            console.error("Error updating user role:", err);
            setToast({ message: 'Failed to update user role.', type: 'error' });
        }
    };

    const handleDeleteUser = async (targetUid) => {
        setActionLoading(true);
        console.log(`[AdminDebug] Initiating deletion for Uid: ${targetUid}`);
        try {
            // Use HTTP endpoint to bypass Gen 2 IAM caching issues
            const idToken = await auth.currentUser.getIdToken();
            let response;
            try {
                response = await fetch(
                    'https://us-central1-a11y-scanner-c052f.cloudfunctions.net/adminDeleteUserHttp',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${idToken}`
                        },
                        body: JSON.stringify({ targetUid })
                    }
                );
            } catch (fetchErr) {
                console.error('[AdminDebug] Fetch failed:', fetchErr);
                throw new Error(`Network error: ${fetchErr.message}`);
            }

            let result;
            try {
                result = await response.json();
            } catch (jsonErr) {
                console.error('[AdminDebug] JSON parse failed:', jsonErr);
                throw new Error(`Server returned invalid response (HTTP ${response.status})`);
            }

            console.log(`[AdminDebug] Deletion response:`, result);

            if (!response.ok) {
                throw new Error(result.error || `HTTP ${response.status}`);
            }

            setUsersList(prev => prev.filter(u => u.id !== targetUid));
            setStats(prev => ({ ...prev, totalUsers: prev.totalUsers - 1 }));
            setShowDeleteConfirm(null);
            setToast({ message: 'User permanently deleted from system.', type: 'success' });
        } catch (err) {
            console.error("[AdminDebug] Error purging user. Full Error:", err);

            // Safely extract error message
            let errMsg = 'Unknown protocol error';
            if (err && typeof err === 'object') {
                errMsg = err.message || err.code || JSON.stringify(err);
            } else if (typeof err === 'string') {
                errMsg = err;
            }

            setToast({
                message: `Deletion failed: ${errMsg}`,
                type: 'error'
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetPassword = async (targetEmail) => {
        setActionLoading(true);
        try {
            const adminResetUserPassword = httpsCallable(functions, 'adminResetUserPassword');
            await adminResetUserPassword({ targetEmail });
            setToast({ message: 'Password reset link generated. Check system logs.', type: 'success' });
        } catch (err) {
            console.error("Error resetting password:", err);
            setToast({ message: 'Failed to reset password.', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateProfile = async (targetUid) => {
        setActionLoading(true);
        try {
            const adminUpdateUserProfile = httpsCallable(functions, 'adminUpdateUserProfile');
            await adminUpdateUserProfile({ targetUid, updates: editData });

            setUsersList(prev => prev.map(u => u.id === targetUid ? { ...u, ...editData } : u));
            setManagingUser(null);
            setToast({ message: 'User profile updated successfully.', type: 'success' });
        } catch (err) {
            console.error("Error updating profile:", err);
            setToast({ message: 'Failed to update profile.', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const filteredScans = useMemo(() => {
        let result = [...recentScans];

        // Date filter
        if (dateFilter !== 'all') {
            const cutoff = new Date();
            if (dateFilter === '24h') cutoff.setHours(cutoff.getHours() - 24);
            else if (dateFilter === '7d') cutoff.setDate(cutoff.getDate() - 7);
            else if (dateFilter === '30d') cutoff.setDate(cutoff.getDate() - 30);

            result = result.filter(s => {
                if (!s.createdAt) return false;
                const scanDate = s.createdAt.toDate();
                return scanDate >= cutoff;
            });
        }

        // Sort
        result.sort((a, b) => {
            const timeA = a.createdAt ? a.createdAt.toMillis() : 0;
            const timeB = b.createdAt ? b.createdAt.toMillis() : 0;
            return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
        });

        return result;
    }, [recentScans, dateFilter, sortOrder]);

    // Filtered and Sorted Users
    const filteredUsers = useMemo(() => {
        let result = [...usersList];

        // Filter
        if (userSearchQuery.trim()) {
            const q = userSearchQuery.toLowerCase();
            result = result.filter(u => 
                (u.displayName || '').toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q) ||
                (u.company || '').toLowerCase().includes(q) ||
                (u.jobTitle || '').toLowerCase().includes(q)
            );
        }

        // Sort
        result.sort((a, b) => {
            let valA, valB;
            if (userSortKey === 'name') {
                valA = (a.displayName || 'Unnamed').toLowerCase();
                valB = (b.displayName || 'Unnamed').toLowerCase();
            } else if (userSortKey === 'company') {
                valA = (a.company || '').toLowerCase();
                valB = (b.company || '').toLowerCase();
            } else if (userSortKey === 'role') {
                valA = (a.role || 'user').toLowerCase();
                valB = (b.role || 'user').toLowerCase();
            } else if (userSortKey === 'date') {
                valA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
                valB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
            }

            if (valA < valB) return userSortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return userSortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [usersList, userSearchQuery, userSortKey, userSortOrder]);

    // Comprehensive admin analytics
    const adminAnalytics = useMemo(() => {
        const uniqueUrls = new Set();
        const uniqueSites = new Set();
        const sourceBreakdown = { extension: 0, agent: 0, dashboard: 0 };
        const severityBreakdown = { critical: 0, serious: 0, moderate: 0, minor: 0 };
        const criteriaStats = {}; // criteriaName -> count
        const trendDataMap = {}; // YYYY-MM-DD -> { riskScoreSum, count, severities }
        const urlFrequency = {}; // url -> count
        const siteFrequency = {}; // hostname -> { scans, violations }
        let totalViolations = 0;
        let completedScans = 0;

        recentScans.forEach(scan => {
            // Unified scan source/type detection
            const isExtension = scan.isExtension || scan.source === 'companion-extension';

            // URL and site tracking
            if (scan.url) {
                uniqueUrls.add(scan.url);
                urlFrequency[scan.url] = (urlFrequency[scan.url] || 0) + 1;
                try {
                    const hostname = new URL(scan.url).hostname;
                    uniqueSites.add(hostname);
                    if (!siteFrequency[hostname]) siteFrequency[hostname] = { scans: 0, violations: 0 };
                    siteFrequency[hostname].scans += 1;
                    siteFrequency[hostname].violations += scan.summary?.totalViolations || 0;
                } catch { }
            }

            // Source tracking
            if (isExtension) sourceBreakdown.extension++;
            else if (scan.claimedAgentId || scan.claimedBy) sourceBreakdown.agent++;
            else sourceBreakdown.dashboard++;

            // Severity & Criteria tracking
            const scanSeverities = { critical: 0, serious: 0, moderate: 0, minor: 0 };
            
            if (scan.summary?.bySeverity) {
                Object.keys(scanSeverities).forEach(sev => {
                    const val = scan.summary.bySeverity[sev] || 0;
                    severityBreakdown[sev] += val;
                    scanSeverities[sev] += val;
                });
            } else if (scan.summary?.ruleStats) {
                Object.values(scan.summary.ruleStats).forEach(rule => {
                    const impact = rule.impact || 'minor';
                    const count = rule.count || 0;
                    if (Object.prototype.hasOwnProperty.call(severityBreakdown, impact)) {
                        severityBreakdown[impact] += count;
                        scanSeverities[impact] += count;
                    }
                });
            }

            // Criteria stats
            if (scan.summary?.ruleStats) {
                Object.entries(scan.summary.ruleStats).forEach(([id, rule]) => {
                    const title = rule.title || id.toUpperCase();
                    criteriaStats[title] = (criteriaStats[title] || 0) + (rule.count || 0);
                });
            }

            // Violations and completion
            if (scan.status === 'completed') {
                completedScans++;
                totalViolations += scan.summary?.totalViolations || 0;
            }

            // Date trends
            const scanDate = scan.createdAt?.toDate ? scan.createdAt.toDate().toISOString().split('T')[0] :
                scan.createdAt?.seconds ? new Date(scan.createdAt.seconds * 1000).toISOString().split('T')[0] : null;
            
            if (scanDate) {
                if (!trendDataMap[scanDate]) {
                    trendDataMap[scanDate] = { count: 0, riskScoreSum: 0, severities: { critical: 0, serious: 0, moderate: 0, minor: 0 } };
                }
                trendDataMap[scanDate].count++;
                trendDataMap[scanDate].riskScoreSum += (scan.summary?.riskScore || 0);
                Object.keys(scanSeverities).forEach(sev => {
                    trendDataMap[scanDate].severities[sev] += scanSeverities[sev];
                });
            }
        });

        // Users created over time
        const usersByDate = {};
        usersList.forEach(u => {
            const created = u.createdAt?.toDate ? u.createdAt.toDate().toISOString().split('T')[0] :
                u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toISOString().split('T')[0] : null;
            if (created) {
                usersByDate[created] = (usersByDate[created] || 0) + 1;
            }
        });

        // Format trends
        const scanTrend = Object.entries(trendDataMap)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, data]) => ({
                date,
                riskScore: Math.round(data.riskScoreSum / data.count),
                severities: data.severities
            }));

        const userTrend = Object.entries(usersByDate).sort((a, b) => a[0].localeCompare(b[0]));
        const maxUserDay = userTrend.length > 0 ? Math.max(...userTrend.map(([, v]) => v)) : 1;

        // Top 10s
        const topUrls = Object.entries(urlFrequency)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([url, count]) => ({ url, count }));

        const topSites = Object.entries(siteFrequency)
            .sort((a, b) => b[1].scans - a[1].scans)
            .slice(0, 10)
            .map(([site, data]) => ({ site, ...data }));

        // Categorical Breakdown (Nivo)
        const criteriaBreakdown = Object.entries(criteriaStats)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([criteria, count]) => ({ criteria, count }));

        // Impact Distribution (Nivo)
        const impactDistribution = [
            { id: 'critical', label: 'Critical', value: severityBreakdown.critical, color: '#B91C1C' },
            { id: 'serious', label: 'Serious', value: severityBreakdown.serious, color: '#B45309' },
            { id: 'moderate', label: 'Moderate', value: severityBreakdown.moderate, color: '#FACC15' },
            { id: 'minor', label: 'Minor', value: severityBreakdown.minor, color: '#0F766E' }
        ];

        return {
            uniqueUrls: uniqueUrls.size,
            uniqueSites: uniqueSites.size,
            sourceBreakdown,
            severityBreakdown,
            severityTrend: scanTrend,
            userTrend,
            maxUserDay,
            topUrls,
            topSites,
            criteriaBreakdown,
            impactDistribution,
            avgViolations: completedScans > 0 ? Math.round(totalViolations / completedScans) : 0,
            completedScans,
        };
    }, [recentScans, usersList]);

    if (loading) return (
        <div className="min-h-screen bg-(--bg) flex items-center justify-center">
            <div className="flex flex-col items-center gap-6">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-4 border-(--accent)/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-(--accent) border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-[0.625rem] text-(--accent) font-black uppercase tracking-[0.5em] animate-pulse">Loading Admin Panel...</p>
            </div>
        </div>
    );

    if (profile?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-(--bg) flex flex-col items-center justify-center p-4">
                <div className="bento-card bg-white border-2 border-(--error) p-12 text-center max-w-md w-full relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-(--error)"></div>
                    <span className="text-5xl block mb-8 grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-125">⛔</span>
                    <h2 className="text-xl font-black text-(--error) mb-4 uppercase tracking-[0.3em] ">Access Denied</h2>
                    <p className="text-(--text-muted) text-[0.6875rem] font-black uppercase tracking-widest leading-loose opacity-80 mb-8">
                        Administrator privileges required.<br />
                        Please return to the main dashboard.
                    </p>
                    <button onClick={() => onViewChange('dashboard')} className="pill-button bg-(--error) text-white border-none w-full justify-center">
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-(--bg) text-(--text) flex flex-col selection:bg-(--accent) selection:text-black">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            <main className="max-w-7xl w-full mx-auto px-6 py-12 flex-1 space-y-12">
                <div className="mb-12">
                    <h1 className="text-4xl font-black text-(--text) tracking-tighter mb-2">Admin Dashboard</h1>
                    <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-(--accent) rounded-full animate-ping"></span>
                        <span className="text-xs font-bold text-(--accent) tracking-[0.3em] uppercase opacity-60">System Command Overview</span>
                    </div>
                </div>

                {/* Stats Overview */}
                <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                    {[
                        { label: 'Total Users', value: stats.totalUsers, color: 'text-(--text)' },
                        { label: 'Admins', value: stats.adminCount, color: 'text-(--accent)' },
                        { label: 'Total Scans', value: analytics.totalScansGlobally, color: 'text-blue-500' },
                        { label: 'Violations', value: analytics.totalViolationsFound.toLocaleString(), color: 'text-(--error)' },
                        { label: 'Unique URLs', value: adminAnalytics.uniqueUrls, color: 'text-emerald-500' },
                        { label: 'Unique Sites', value: adminAnalytics.uniqueSites, color: 'text-purple-500' },
                        { label: 'Extension', value: adminAnalytics.sourceBreakdown.extension, color: 'text-blue-500' },
                        { label: 'Agent', value: adminAnalytics.sourceBreakdown.agent, color: 'text-emerald-500' },
                    ].map((s, idx) => (
                        <div key={idx} className="bento-card bg-white p-6 relative overflow-hidden group hover:border-(--accent)/50 transition-all">
                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider mb-2 block opacity-50">{s.label}</span>
                            <span className={`text-2xl font-black ${s.color}`}>{s.value}</span>
                        </div>
                    ))}
                </section>

                {/* Tab Navigation */}
                <div className="flex items-center gap-8 border-b-2 border-(--border) relative">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`text-xs font-black uppercase tracking-widest pt-3 pb-6 transition-all relative -mb-[2px] border-b-2 ${activeTab === 'users' ? 'text-(--accent) border-(--accent)' : 'text-(--text-muted) hover:text-(--text) border-transparent'}`}
                    >
                        User Directory
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`text-xs font-black uppercase tracking-widest pt-3 pb-6 transition-all relative -mb-[2px] border-b-2 ${activeTab === 'analytics' ? 'text-(--accent) border-(--accent)' : 'text-(--text-muted) hover:text-(--text) border-transparent'}`}
                    >
                        Global Scan History
                    </button>
                    <button
                        onClick={() => setActiveTab('platform')}
                        className={`text-xs font-black uppercase tracking-widest pt-3 pb-6 transition-all relative -mb-[2px] border-b-2 ${activeTab === 'platform' ? 'text-(--accent) border-(--accent)' : 'text-(--text-muted) hover:text-(--text) border-transparent'}`}
                    >
                        Platform Analytics
                    </button >
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`text-xs font-black uppercase tracking-widest pt-3 pb-6 transition-all relative -mb-[2px] border-b-2 ${activeTab === 'settings' ? 'text-(--accent) border-(--accent)' : 'text-(--text-muted) hover:text-(--text) border-transparent'}`}
                    >
                        Settings
                    </button>
                    <button
                        onClick={() => setActiveTab('plans')}
                        className={`text-xs font-black uppercase tracking-widest pt-3 pb-6 transition-all relative -mb-[2px] border-b-2 ${activeTab === 'plans' ? 'text-(--accent) border-(--accent)' : 'text-(--text-muted) hover:text-(--text) border-transparent'}`}
                    >
                        Billing Plans
                    </button>
                    <button
                        onClick={() => setActiveTab('notifications')}
                        className={`text-xs font-black uppercase tracking-widest pt-3 pb-6 transition-all relative -mb-[2px] border-b-2 ${activeTab === 'notifications' ? 'text-(--accent) border-(--accent)' : 'text-(--text-muted) hover:text-(--text) border-transparent'}`}
                    >
                        Command Alerts {adminNotifications.length > 0 && `(${adminNotifications.length})`}
                    </button>
                </div>

                {activeTab === 'notifications' && (
                    <section className="grid lg:grid-cols-[1fr_1.3fr] gap-8 animate-in fade-in duration-500">
                        <div className="bento-card bg-white p-8 space-y-6">
                            <div>
                                <h2 className="text-lg font-black uppercase tracking-[0.25em]">Broadcast Center</h2>
                                <p className="text-[0.5625rem] text-(--text-muted) font-black uppercase tracking-[0.3em] mt-2">Message every authenticated user</p>
                            </div>
                            <form onSubmit={publishBroadcast} className="space-y-4">
                                <input value={broadcast.title} onChange={e => setBroadcast({ ...broadcast, title: e.target.value })} placeholder="ANNOUNCEMENT TITLE" maxLength={120} className="w-full rounded-xl border border-(--border) px-4 py-3 text-xs font-bold bg-(--surface-stone)" />
                                <textarea value={broadcast.message} onChange={e => setBroadcast({ ...broadcast, message: e.target.value })} placeholder="MESSAGE" maxLength={1000} rows={5} className="w-full rounded-xl border border-(--border) px-4 py-3 text-xs font-bold bg-(--surface-stone)" />
                                <select value={broadcast.severity} onChange={e => setBroadcast({ ...broadcast, severity: e.target.value })} className="w-full rounded-xl border border-(--border) px-4 py-3 text-xs font-bold bg-(--surface-stone)">
                                    <option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option>
                                </select>
                                <button className="pill-button bg-(--accent) text-black border-none w-full justify-center">Publish Broadcast</button>
                            </form>
                        </div>
                        <div className="space-y-8">
                            <div className="bento-card bg-white p-8">
                                <h2 className="text-lg font-black uppercase tracking-[0.25em] mb-5">Security Alerts</h2>
                                <div className="space-y-3 max-h-72 overflow-y-auto">
                                    {adminNotifications.length === 0 ? <p className="text-xs text-(--text-muted)">No active command alerts.</p> : adminNotifications.map(item => <div key={item.id} className="border-l-4 border-(--error) bg-(--surface-stone) p-4"><div className="text-[0.6rem] font-black uppercase tracking-widest">{item.title || item.type}</div><p className="text-xs mt-1">{item.message}</p></div>)}
                                </div>
                            </div>
                            <div className="bento-card bg-white p-8">
                                <h2 className="text-lg font-black uppercase tracking-[0.25em] mb-5">Admin Activity</h2>
                                <div className="space-y-2 max-h-72 overflow-y-auto">{adminActivity.length === 0 ? <p className="text-xs text-(--text-muted)">No activity recorded.</p> : adminActivity.map(item => <div key={item.id} className="flex justify-between gap-4 border-b border-(--border) py-3 text-xs"><span>{item.action || item.type}</span><span className="text-(--text-muted) text-[0.6rem]">{item.targetUid || item.actorUid || 'system'}</span></div>)}</div>
                            </div>
                        </div>
                    </section>
                )}

                {/* User Directory Tab */}                {
                    activeTab === 'users' && (
                        <section className="animate-in fade-in duration-500">
                            <div className="bento-card bg-white overflow-hidden shadow-sm">
                                <div className="p-10 border-b border-(--border) flex flex-col sm:flex-row sm:items-center justify-between gap-8">
                                    <div>
                                        <h2 className="text-lg font-black text-(--text) uppercase tracking-[0.3em]">User Directory</h2>
                                        <p className="text-[0.5625rem] text-(--accent) font-black uppercase tracking-[0.4em] mt-2 opacity-50">Authorized Members</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-6">
                                        <div className="flex items-center gap-4 bg-(--surface-stone) px-6 py-3 rounded-2xl border border-(--border) min-w-[15rem]">
                                            <span className="text-xs opacity-40">🔍</span>
                                            <input 
                                                type="text" 
                                                placeholder="SEARCH USERS..." 
                                                value={userSearchQuery}
                                                onChange={(e) => setUserSearchQuery(e.target.value)}
                                                className="bg-transparent border-none text-[0.625rem] font-black text-(--text) uppercase tracking-widest focus:outline-none w-full"
                                            />
                                        </div>
                                        <div className="flex items-center gap-4 bg-(--surface-stone) px-6 py-3 rounded-2xl border border-(--border)">
                                            <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-40">Sort:</span>
                                            <select 
                                                value={userSortKey} 
                                                onChange={(e) => setUserSortKey(e.target.value)}
                                                className="bg-transparent border-none text-[0.625rem] font-black text-(--accent) uppercase tracking-widest focus:outline-none cursor-pointer"
                                            >
                                                <option value="name" className="bg-(--bg)">Name</option>
                                                <option value="company" className="bg-(--bg)">Company</option>
                                                <option value="role" className="bg-(--bg)">Role</option>
                                                <option value="date" className="bg-(--bg)">Joined</option>
                                            </select>
                                            <button 
                                                onClick={() => setUserSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                                className="text-[0.625rem] font-black text-(--accent) uppercase tracking-widest hover:opacity-70 transition-opacity"
                                            >
                                                {userSortOrder === 'asc' ? '↑' : '↓'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-(--border) bg-(--surface-stone)/30">
                                            <th className="px-10 py-6 text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-50">User</th>
                                            <th className="px-8 py-6 text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-50">Information</th>
                                            <th className="px-8 py-6 text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-50">Role</th>
                                            <th className="px-8 py-6 text-right text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-50">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-(--border)">
                                        {filteredUsers.map(u => (

                                            <tr key={u.id} className="hover:bg-(--surface-stone) transition-colors group/row">
                                                <td className="px-8 py-8">
                                                    <div className="flex items-center gap-5">
                                                        <div className="w-12 h-12 rounded-2xl bg-(--surface-stone) border border-(--border) flex items-center justify-center font-black text-(--text-muted) shrink-0 group-hover/row:border-(--accent)/40 transition-all text-sm">
                                                            {u.displayName ? u.displayName.charAt(0).toUpperCase() : u.email ? u.email.charAt(0).toUpperCase() : '?'}
                                                        </div>
                                                        <div>
                                                            <p className="font-black text-(--text) uppercase tracking-widest">{u.displayName || 'Anonymous User'}</p>
                                                            <p className="text-[0.5625rem] text-(--text-muted) font-black uppercase tracking-wider mt-1">{u.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-8">
                                                    <div className="text-[0.5625rem] space-y-2 text-(--text-muted) font-black uppercase tracking-widest leading-relaxed">
                                                        {u.company && <p className="flex gap-2"><span className="text-(--accent) opacity-40">Organization:</span> {u.company}</p>}
                                                        {u.jobTitle && <p className="flex gap-2"><span className="text-(--accent) opacity-40">Position:</span> {u.jobTitle}</p>}
                                                        {u.phone && <p className="flex gap-2"><span className="text-(--accent) opacity-40">Phone:</span> {u.phone}</p>}
                                                        {!u.company && !u.jobTitle && !u.phone && <span className="italic opacity-30">No profile info</span>}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-8">
                                                    <span className={`px-4 py-1.5 rounded-full text-[0.5625rem] font-black tracking-[0.2em] uppercase border ${u.role === 'admin' ? 'border-(--accent) text-(--accent) bg-(--accent)/5' : 'border-(--border) text-(--text-muted) bg-transparent opacity-50'}`}>
                                                        {u.role ? u.role.toUpperCase() : 'USER'}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-8 text-right">
                                                    <div className="flex items-center justify-end gap-3 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                                        {u.id !== user.uid && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        setManagingUser(u.id);
                                                                        setEditData({
                                                                            displayName: u.displayName || '',
                                                                            company: u.company || '',
                                                                            jobTitle: u.jobTitle || ''
                                                                        });
                                                                    }}
                                                                    className="p-2 hover:bg-(--surface-stone) rounded-lg text-(--accent) transition-colors"
                                                                    title="Edit Profile"
                                                                >
                                                                    <span className="text-xs">✏️</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleResetPassword(u.email)}
                                                                    className="p-2 hover:bg-(--surface-stone) rounded-lg text-blue-500 transition-colors"
                                                                    title="Reset Password"
                                                                    disabled={actionLoading}
                                                                >
                                                                    <span className="text-xs">🔑</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRoleChange(u.id, u.role)}
                                                                    className={`px-4 py-2 rounded-xl text-[0.5625rem] font-black uppercase tracking-widest border transition-all ${u.role === 'admin' ? 'border-(--error) text-(--error) hover:bg-(--error)/10' : 'border-(--accent) text-(--accent) hover:bg-(--accent)/10'}`}
                                                                >
                                                                    {u.role === 'admin' ? 'Revoke' : 'Admin'}
                                                                </button>
                                                                <button
                                                                    onClick={() => setShowDeleteConfirm(u.id)}
                                                                    className="p-2 hover:bg-(--error)/10 rounded-lg text-(--error) transition-colors"
                                                                    title="Delete User"
                                                                >
                                                                    <span className="text-xs">🗑️</span>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )
                }

                {/* Global Scan History Tab */}
                {
                    activeTab === 'analytics' && (
                        <section className="bento-card bg-white overflow-hidden animate-in fade-in duration-500 shadow-sm">
                            <div className="p-8 border-b border-(--border) bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-8">
                                <div>
                                    <h2 className="text-lg font-black text-(--text) uppercase tracking-[0.3em]">Global Scan History</h2>
                                    <p className="text-[0.5625rem] text-(--accent) font-black uppercase tracking-[0.4em] mt-2 opacity-50">Global Activity</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-6">
                                    <div className="flex items-center gap-4 bg-(--surface-stone) px-6 py-3 rounded-2xl border border-(--border)">
                                        <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-40">Filter:</span>
                                        <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="bg-transparent border-none text-[0.625rem] font-black text-(--accent) uppercase tracking-widest focus:outline-none cursor-pointer">
                                            <option value="all" className="bg-(--bg)">All Time</option>
                                            <option value="24h" className="bg-(--bg)">24 Hours</option>
                                            <option value="7d" className="bg-(--bg)">7 Days</option>
                                            <option value="30d" className="bg-(--bg)">30 Days</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-4 bg-(--surface-stone) px-6 py-3 rounded-2xl border border-(--border)">
                                        <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.3em] opacity-40">Sort:</span>
                                        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="bg-transparent border-none text-[0.625rem] font-black text-(--accent) uppercase tracking-widest focus:outline-none cursor-pointer">
                                            <option value="desc" className="bg-(--bg)">Recent</option>
                                            <option value="asc" className="bg-(--bg)">Oldest</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="overflow-x-auto p-8 max-h-[50em] overflow-y-auto">
                                {filteredScans.length === 0 ? (
                                    <div className="text-center py-20 text-[0.625rem] text-(--text-muted) font-black uppercase tracking-[0.3em] opacity-40">No records found.</div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredScans.map(scan => {
                                            const riskScore = scan.summary?.riskScore || (scan.summary?.bySeverity ? scan.summary.bySeverity.critical * 10 + scan.summary.bySeverity.serious * 5 + scan.summary.bySeverity.moderate * 2 + (scan.summary.bySeverity.minor || 0) : 0);
                                            const gradeInfo = getGradeFromRisk(riskScore);
                                            const isExtension = scan.isExtension || scan.source === 'companion-extension';
                                            return (
                                                <div key={scan.id} className="p-5 bento-card bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-6 hover:border-(--accent)/40 transition-all group/item">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center gap-4">
                                                            <div className={`w-10 h-10 flex items-center justify-center rounded-2xl text-sm font-black border ${gradeInfo.pass ? 'border-(--pass-green) text-(--pass-green) bg-(--pass-green)/5' : 'border-(--fail-red) text-(--fail-red) bg-(--fail-red)/5'}`}>{gradeInfo.grade}</div>
                                                            <div className="flex flex-col">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`px-2 py-0.5 rounded text-[0.5rem] font-black uppercase tracking-widest border ${scan?.status === 'completed' ? 'border-(--pass-green) text-(--pass-green) bg-(--pass-green)/5' : scan?.status === 'failed' ? 'border-(--fail-red) text-(--fail-red) bg-(--fail-red)/5' : 'border-blue-500 text-blue-500 bg-blue-500/5'}`}>
                                                                        {scan?.status ? scan.status.toUpperCase() : 'QUEUED'}
                                                                    </span>
                                                                    <span className="text-(--text) font-bold text-xs truncate max-w-xs group-hover/item:text-(--accent) transition-colors">{scan.url}</span>
                                                                </div>
                                                                <span className="text-[10px] font-bold text-(--text-muted) uppercase tracking-widest mt-1">
                                                                    {scan.createdAt ? scan.createdAt.toDate().toLocaleString() : 'Pending...'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-8">
                                                        <div className="flex flex-col items-end gap-1">
                                                            <span className="text-[0.5rem] font-bold text-(--text-muted) uppercase tracking-widest opacity-40">Identity</span>
                                                            <span className="text-[0.625rem] font-mono font-bold text-espresso/40">
                                                                {scan.id.slice(0, 8).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1">
                                                            <span className="text-[0.5rem] font-bold text-(--text-muted) uppercase tracking-widest opacity-40">Source</span>
                                                            <span className={`text-[0.625rem] font-black uppercase tracking-widest ${isExtension ? 'text-blue-500' : 'text-emerald-500'}`}>
                                                                {isExtension ? 'Extension' : (scan.claimedAgentId ? `Agent-${scan.claimedAgentId.slice(0, 4).toUpperCase()}` : 'Web App')}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 min-w-[4rem]">
                                                            <span className="text-[0.5rem] font-bold text-(--text-muted) uppercase tracking-widest opacity-40">Violations</span>
                                                            <span className={`text-sm font-black ${scan.summary?.totalViolations > 0 ? 'text-(--fail-red)' : 'text-(--pass-green)'}`}>
                                                                {scan.summary?.totalViolations ? scan.summary.totalViolations.toString().padStart(3, '0') : '000'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </section>
                    )
                }

                {/* Platform Analytics Tab */}
                {
                    activeTab === 'platform' && (
                        <section className="space-y-10 animate-in fade-in duration-500">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                                {[
                                    { label: 'Avg Violations', value: adminAnalytics.avgViolations, color: 'text-(--accent)' },
                                    { label: 'Dashboard Scans', value: adminAnalytics.sourceBreakdown.dashboard, color: 'text-amber-500' },
                                    { label: 'Completed Scans', value: adminAnalytics.completedScans, color: 'text-(--pass-green)' },
                                    { label: 'Total Users', value: stats.totalUsers, color: 'text-(--text)' },
                                ].map((s, idx) => (
                                    <div key={idx} className="bento-card bg-white p-8">
                                        <span className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] mb-3 block opacity-50">{s.label}</span>
                                        <span className={`text-4xl font-black ${s.color}`}>{s.value}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Scan Trend (Tremor) */}
                                <div className="bento-card bg-white p-8">
                                    <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">Global Scan Trends</h3>
                                    <SeverityTrendTremor data={adminAnalytics.severityTrend} height={350} />
                                </div>

                                {/* User Trend (Manual or Selective Selective) */}
                                <div className="bento-card bg-white p-8">
                                    <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">User Growth</h3>
                                    {adminAnalytics.userTrend.length > 0 ? (
                                        <div className="flex items-end gap-1.5 h-[12.5em] overflow-x-auto custom-scrollbar pb-4 pt-10">
                                            {adminAnalytics.userTrend.slice(-60).map(([date, count]) => (
                                                <div key={date} className="flex flex-col items-center gap-1.5 min-w-[1rem] group" title={`${date}: ${count} users`}>
                                                    <span className="text-[0.5rem] font-black text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity">{count}</span>
                                                    <div className="w-3 bg-purple-500/20 group-hover:bg-purple-500/40 rounded transition-colors relative" style={{ height: `${Math.max(4, (count / adminAnalytics.maxUserDay) * 160)}px` }}>
                                                        <div className="absolute bottom-0 left-0 w-full bg-purple-500 rounded h-full origin-bottom transform scale-y-100 transition-transform duration-1000"></div>
                                                    </div>
                                                    <span className="text-[0.5rem] font-bold text-(--text-muted) opacity-30 -rotate-45 origin-top-left whitespace-nowrap">{date.slice(5)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-[0.625rem] text-(--text-muted) font-bold uppercase tracking-widest text-center py-12 opacity-50">No data available</p>
                                    )}
                                </div>

                                {/* WCAG Criteria Breakdown (Nivo) */}
                                <div className="bento-card bg-white p-8">
                                    <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">Top 10 Violations</h3>
                                    <WcagCriteriaBreakdownNivo data={adminAnalytics.criteriaBreakdown} />
                                </div>

                                {/* Severity Distribution (Nivo) */}
                                <div className="bento-card bg-white p-8">
                                    <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">Global Severity Distribution</h3>
                                    <ImpactPieChartNivo data={adminAnalytics.impactDistribution} />
                                </div>

                                {/* Top Scanned URLs */}
                                <div className="bento-card bg-white p-8">
                                    <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">Top Scanned URLs</h3>
                                    <ul className="space-y-3 max-h-[25em] overflow-y-auto custom-scrollbar">
                                        {adminAnalytics.topUrls.map((item, i) => (
                                            <li key={item.url} className="flex items-center justify-between gap-4 p-4 bg-(--surface-stone) border border-(--border) rounded-2xl hover:border-(--accent) transition-colors group/url">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className="text-[0.625rem] font-black text-(--accent) opacity-40 w-5">#{i + 1}</span>
                                                    <span className="text-xs font-bold text-(--text) truncate tracking-tight group-hover/url:text-(--accent) transition-colors">{item.url}</span>
                                                </div>
                                                <span className="text-[0.625rem] font-black text-(--accent) bg-(--accent)/10 px-4 py-1.5 rounded-full shrink-0 uppercase">{item.count} scans</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Top Scanned Sites */}
                                <div className="bento-card bg-white p-8">
                                    <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">Top Domain Analytics</h3>
                                    <ul className="space-y-3 max-h-[25em] overflow-y-auto custom-scrollbar">
                                        {adminAnalytics.topSites.map((item, i) => (
                                            <li key={item.site} className="flex items-center justify-between gap-4 p-4 bg-(--surface-stone) border border-(--border) rounded-2xl hover:border-orange-500/50 transition-colors">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className="text-[0.625rem] font-black text-(--accent) opacity-40 w-5">#{i + 1}</span>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="text-xs font-black text-(--text) tracking-tight truncate">{item.site}</span>
                                                        <span className="text-[0.5625rem] font-bold text-(--text-muted) uppercase tracking-widest opacity-60 mt-0.5">{item.scans} SCANS • {item.violations} ISSUES</span>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Source Breakdown */}
                            <div className="bento-card bg-white p-8">
                                <h3 className="text-sm font-black uppercase text-(--text) tracking-widest border-b border-(--border) pb-6 mb-6">Platform Scan Source Distribution</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    {[
                                        { label: 'Extension', value: adminAnalytics.sourceBreakdown.extension, color: 'bg-blue-500' },
                                        { label: 'Agent Hub', value: adminAnalytics.sourceBreakdown.agent, color: 'bg-emerald-500' },
                                        { label: 'Web Console', value: adminAnalytics.sourceBreakdown.dashboard, color: 'bg-amber-500' },
                                    ].map(s => {
                                        const total = adminAnalytics.sourceBreakdown.extension + adminAnalytics.sourceBreakdown.agent + adminAnalytics.sourceBreakdown.dashboard;
                                        const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
                                        return (
                                            <div key={s.label} className="space-y-3 p-6 bg-(--surface-stone) rounded-2xl border border-(--border)">
                                                <div className="flex justify-between items-end">
                                                    <span className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest">{s.label}</span>
                                                    <span className="text-xl font-black text-(--text)">{pct}%</span>
                                                </div>
                                                <div className="h-4 bg-white rounded-full overflow-hidden border border-(--border)">
                                                    <div className={`h-full ${s.color} transition-all duration-1000`} style={{ width: `${pct}%` }}></div>
                                                </div>
                                                <p className="text-[0.5625rem] font-bold text-(--text-muted) uppercase tracking-widest opacity-40">{s.value} TOTAL SCANS</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>
                    )
                }

                {/* Settings Tab */}
                {
                    activeTab === 'settings' && (
                        <section className="bento-card bg-white overflow-hidden animate-in fade-in duration-500 shadow-sm">
                            <div className="p-8 border-b border-(--border) bg-white">
                                <h2 className="text-lg font-black text-(--text) uppercase tracking-[0.3em]">Platform Settings</h2>
                                <p className="text-[0.5625rem] text-(--text-muted) font-black uppercase tracking-[0.4em] mt-2 opacity-50">Configure public-facing options</p>
                            </div>
                            <div className="p-8 space-y-8">
                                <div className="bento-card bg-white border border-(--border) p-8 space-y-6">
                                    <div className="flex items-start justify-between gap-8">
                                        <div className="space-y-2">
                                            <h3 className="text-sm font-black uppercase tracking-widest text-(--text)">Live Agent Count Refresh</h3>
                                            <p className="text-[0.625rem] font-medium text-(--text-muted) uppercase tracking-wider leading-relaxed max-w-lg">
                                                Controls how often the public landing page refreshes the live node count displayed to visitors.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4 bg-(--surface-stone) px-4 py-2 rounded-2xl border border-(--border)">
                                            <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] opacity-50">Every</span>
                                            <select value={agentCountRefreshMinutes} onChange={(e) => setAgentCountRefreshMinutes(Number(e.target.value))} className="bg-transparent border-none text-sm font-black text-(--accent) uppercase tracking-widest focus:outline-none cursor-pointer">
                                                <option value={1} className="bg-(--bg)">1 min</option>
                                                <option value={2} className="bg-(--bg)">2 min</option>
                                                <option value={5} className="bg-(--bg)">5 min</option>
                                                <option value={10} className="bg-(--bg)">10 min</option>
                                                <option value={15} className="bg-(--bg)">15 min</option>
                                                <option value={30} className="bg-(--bg)">30 min</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="bento-card bg-white border border-(--border) p-8 space-y-6">
                                    <div className="flex flex-col gap-8">
                                        <div className="space-y-2">
                                            <h3 className="text-sm font-black uppercase tracking-widest text-(--text)">Extension Store Links</h3>
                                            <p className="text-[0.625rem] font-medium text-(--text-muted) uppercase tracking-wider leading-relaxed max-w-lg">
                                                Update the links where users can download the Chrome or Edge extensions.
                                            </p>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="flex flex-col gap-2">
                                                <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] opacity-80">Chrome Store URL</label>
                                                <input 
                                                    type="url" 
                                                    placeholder="https://chrome.google.com/..." 
                                                    value={chromeStoreLink}
                                                    onChange={e => setChromeStoreLink(e.target.value)}
                                                    className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black text-(--text) focus:border-(--accent) focus:outline-none transition-all" 
                                                />
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] opacity-80">Edge Add-ons URL</label>
                                                <input 
                                                    type="url" 
                                                    placeholder="https://microsoftedge.microsoft.com/..." 
                                                    value={edgeStoreLink}
                                                    onChange={e => setEdgeStoreLink(e.target.value)}
                                                    className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black text-(--text) focus:border-(--accent) focus:outline-none transition-all" 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end pt-4">
                                    <button onClick={handleSavePublicConfig} disabled={actionLoading} className="pill-button justify-center min-w-[12.5em]">
                                        {actionLoading ? 'Saving...' : 'Save Settings'}
                                    </button>
                                </div>
                            </div>
                        </section>
                    )
                }

                {/* Plans Tab */}
                {
                    activeTab === 'plans' && (
                        <section className="animate-in fade-in duration-500">
                            <div className="bento-card bg-white overflow-hidden shadow-sm">
                                <div className="p-10 border-b border-(--border) flex flex-col sm:flex-row sm:items-center justify-between gap-8">
                                    <div>
                                        <h2 className="text-lg font-black text-(--text) uppercase tracking-[0.3em]">Billing Plans</h2>
                                        <p className="text-[0.5625rem] text-(--accent) font-black uppercase tracking-[0.4em] mt-2 opacity-50">Manage Subscriptions & Features</p>
                                    </div>
                                    <button 
                                         onClick={() => setManagingPlan({ name: '', tier: 10, price: 0, stripePriceId: '', active: false, maxDomains: 1, maxPagesPerMonth: 100, maxJourneys: 0, maxLocalAgents: 1, historyRetentionDays: 7, allowScheduling: false, allowLocalAgent: false, allowCompanion: true, allowPdfExport: false, allowSharing: false })}
                                        className="pill-button justify-center min-w-[12.5em]"
                                    >
                                        Create New Plan
                                    </button>
                                </div>
                                <div className="p-8 space-y-6">
                                    {plans.length === 0 ? (
                                        <p className="text-[0.625rem] text-(--text-muted) font-bold uppercase tracking-widest text-center py-12 opacity-50">No plans configured</p>
                                    ) : (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8 xl:grid-cols-3 gap-6">
                                            {plans.map((plan) => (
                                                <div key={plan.id} className="bento-card bg-(--surface-stone) border border-(--border) p-8 flex flex-col gap-6 relative group/plan">
                                                    {!plan.active && (
                                                        <div className="absolute top-4 right-4 bg-(--text-muted) text-white text-[0.5rem] font-black tracking-widest uppercase px-3 py-1 rounded-full">Inactive</div>
                                                    )}
                                                    {plan.active && (
                                                        <div className="absolute top-4 right-4 bg-(--pass-green) text-white text-[0.5rem] font-black tracking-widest uppercase px-3 py-1 rounded-full">Active</div>
                                                    )}
                                                    
                                                    <div className="space-y-2">
                                                        <h3 className="text-2xl font-black text-(--text) tracking-tighter">{plan.name}</h3>
                                                        <div className="flex items-end gap-1">
                                                            <span className="text-xl font-black text-(--accent)">${plan.price}</span>
                                                            <span className="text-[0.625rem] font-bold text-(--text-muted) uppercase tracking-widest mb-1">/ mo</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex-1 space-y-4">
                                                        <p className="text-[0.5625rem] text-(--text-muted) font-black uppercase tracking-[0.2em] border-b border-(--border) pb-2">Limits & Features</p>
                                                        <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                                                            <li className="flex justify-between text-xs font-bold text-(--text-muted)">
                                                                <span>Max Domains:</span> <span className="text-(--text)">{plan.maxDomains ?? 1}</span>
                                                            </li>
                                                            <li className="flex justify-between text-xs font-bold text-(--text-muted)">
                                                                <span>Pages / Month:</span> <span className="text-(--text)">{plan.maxPagesPerMonth ?? 100}</span>
                                                            </li>
                                                            <li className="flex justify-between text-xs font-bold text-(--text-muted)">
                                                                <span>Scheduling:</span> <span className="text-(--text)">{plan.allowScheduling ? '✓ Yes' : '✕ No'}</span>
                                                            </li>
                                                            <li className="flex justify-between text-xs font-bold text-(--text-muted)">
                                                                <span>Local Agent:</span> <span className="text-(--text)">{plan.allowLocalAgent ? '✓ Yes' : '✕ No'}</span>
                                                            </li>
                                                            <li className="flex justify-between text-xs font-bold text-(--text-muted)">
                                                                <span>Companion App:</span> <span className="text-(--text)">{plan.allowCompanion ? '✓ Yes' : '✕ No'}</span>
                                                            </li>
                                                            <li className="flex justify-between text-xs font-bold text-(--text-muted)">
                                                                <span>PDF Export:</span> <span className="text-(--text)">{plan.allowPdfExport ? '✓ Yes' : '✕ No'}</span>
                                                            </li>
                                                        </ul>
                                                    </div>

                                                    <div className="flex flex-col gap-2 pt-4 border-t border-(--border)">
                                                        <p className="text-[0.5rem] font-mono text-(--text-muted) truncate" title={plan.stripePriceId}>
                                                            Stripe ID: {plan.stripePriceId || 'None'}
                                                        </p>
                                                        <p className="text-[0.5rem] font-mono text-(--text-muted)">
                                                            Sort Tier: {plan.tier}
                                                        </p>
                                                        <div className="flex gap-3 mt-4">
                                                            <button 
                                                                onClick={() => setManagingPlan({ ...plan })}
                                                                className="flex-1 py-3 bg-white border border-(--border) hover:border-(--accent) text-(--text) hover:text-(--accent) rounded-xl text-[0.5625rem] font-black tracking-widest uppercase transition-all"
                                                            >
                                                                Edit Details
                                                            </button>
                                                            <button 
                                                                onClick={() => setShowPlanDeleteConfirm(plan.id)}
                                                                className="py-3 px-4 bg-white border border-(--border) hover:border-(--error)/50 hover:bg-(--error)/5 text-(--error) rounded-xl text-[0.5625rem] font-black tracking-widest uppercase transition-all"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    )
                }
            </main >

            {/* Edit Profile Modal */}
            {
                managingUser && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
                        <div className="bento-card bg-white p-10 max-w-lg w-full relative overflow-hidden">
                            <h2 className="text-xl font-black text-(--text) uppercase tracking-[0.3em] mb-8 ">Modify User Profile</h2>
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Display Name</label>
                                    <input type="text" value={editData.displayName} onChange={(e) => setEditData({ ...editData, displayName: e.target.value })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Organization</label>
                                        <input type="text" value={editData.company} onChange={(e) => setEditData({ ...editData, company: e.target.value })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Job Title</label>
                                        <input type="text" value={editData.jobTitle} onChange={(e) => setEditData({ ...editData, jobTitle: e.target.value })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-4 mt-10">
                                <button onClick={() => setManagingUser(null)} className="flex-1 pill-button bg-transparent text-(--text-muted) border border-(--border) justify-center">Cancel</button>
                                <button onClick={() => handleUpdateProfile(managingUser)} disabled={actionLoading} className="flex-1 pill-button justify-center disabled:opacity-50">
                                    {actionLoading ? 'Saving...' : 'Commit Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-in zoom-in-95 duration-300">
                        <div className="bento-card bg-white p-12 text-center max-w-2xl w-full relative overflow-hidden border-2 border-(--error)">
                            <div className="absolute top-0 left-0 w-full h-1 bg-(--error)"></div>
                            <span className="text-6xl block mb-8 animate-bounce">⚠️</span>
                            <h2 className="text-2xl font-black text-(--error) mb-6 uppercase tracking-[0.4em] ">System Purge</h2>
                            <p className="text-(--text-muted) text-[0.75rem] font-black uppercase tracking-widest leading-loose opacity-80 mb-10">
                                You are about to permanently delete this user account. All associated data, scan history, and access tokens will be irrevocably destroyed.
                                <br /><br />
                                <span className="text-(--text)">This action cannot be undone.</span>
                            </p>
                            <div className="flex gap-4">
                                <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 pill-button bg-white text-(--text) border-2 border-(--border) justify-center">Abort Mission</button>
                                <button onClick={() => handleDeleteUser(showDeleteConfirm)} disabled={actionLoading} className="flex-1 pill-button bg-(--error) text-white border-none justify-center disabled:opacity-50">
                                    {actionLoading ? 'Purging...' : 'Execute Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Plan Edit Modal */}
            {
                managingPlan && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto">
                        <div className="bento-card bg-white p-10 max-w-2xl w-full relative overflow-hidden my-auto max-h-[90vh] flex flex-col">
                            <h2 className="text-xl font-black text-(--text) uppercase tracking-[0.3em] mb-8 shrink-0">{managingPlan.id ? 'Edit Plan' : 'Create Plan'}</h2>
                            
                            <div className="space-y-6 overflow-y-auto custom-scrollbar pr-4 flex-1">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Plan Name</label>
                                        <input type="text" placeholder="e.g. Pro, Starter" value={managingPlan.name} onChange={(e) => setManagingPlan({ ...managingPlan, name: e.target.value })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Price / Month ($)</label>
                                        <input type="number" placeholder="50" value={managingPlan.price} onChange={(e) => setManagingPlan({ ...managingPlan, price: Number(e.target.value) })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Stripe Price ID</label>
                                        <input type="text" placeholder="price_1ABC123..." value={managingPlan.stripePriceId} onChange={(e) => setManagingPlan({ ...managingPlan, stripePriceId: e.target.value })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-mono font-bold text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Sort Tier (Higher = Right)</label>
                                        <input type="number" placeholder="10" value={managingPlan.tier} onChange={(e) => setManagingPlan({ ...managingPlan, tier: Number(e.target.value) })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black uppercase tracking-widest text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="flex items-center gap-3 cursor-pointer mt-4">
                                        <input 
                                            type="checkbox" 
                                            checked={managingPlan.active} 
                                            onChange={(e) => setManagingPlan({ ...managingPlan, active: e.target.checked })}
                                            className="w-5 h-5 accent-(--accent) cursor-pointer"
                                        />
                                        <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)">Publicly Visible</span>
                                    </label>
                                </div>

                                <div className="space-y-6 pt-4 border-t border-(--border)">
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Max Domains</label>
                                            <input type="number" placeholder="1" value={managingPlan.maxDomains ?? 1} onChange={(e) => setManagingPlan({ ...managingPlan, maxDomains: Number(e.target.value) })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Max Pages / Month</label>
                                            <input type="number" placeholder="100" value={managingPlan.maxPagesPerMonth ?? 100} onChange={(e) => setManagingPlan({ ...managingPlan, maxPagesPerMonth: Number(e.target.value) })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Max Journeys</label>
                                            <input type="number" placeholder="0" value={managingPlan.maxJourneys ?? 0} onChange={(e) => setManagingPlan({ ...managingPlan, maxJourneys: Number(e.target.value) })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[0.5625rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 pl-2">Max Local Agents</label>
                                            <input type="number" placeholder="1" value={managingPlan.maxLocalAgents ?? 1} onChange={(e) => setManagingPlan({ ...managingPlan, maxLocalAgents: Number(e.target.value) })} className="w-full bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-xs font-black text-(--text) focus:border-(--accent) focus:outline-none transition-all" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" checked={managingPlan.allowScheduling ?? false} onChange={(e) => setManagingPlan({ ...managingPlan, allowScheduling: e.target.checked })} className="w-5 h-5 accent-(--accent) cursor-pointer" />
                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)">Allow Scheduling</span>
                                        </label>
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" checked={managingPlan.allowLocalAgent ?? false} onChange={(e) => setManagingPlan({ ...managingPlan, allowLocalAgent: e.target.checked })} className="w-5 h-5 accent-(--accent) cursor-pointer" />
                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)">Allow Local Agent</span>
                                        </label>
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" checked={managingPlan.allowCompanion ?? true} onChange={(e) => setManagingPlan({ ...managingPlan, allowCompanion: e.target.checked })} className="w-5 h-5 accent-(--accent) cursor-pointer" />
                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)">Allow Companion</span>
                                        </label>
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" checked={managingPlan.allowPdfExport ?? false} onChange={(e) => setManagingPlan({ ...managingPlan, allowPdfExport: e.target.checked })} className="w-5 h-5 accent-(--accent) cursor-pointer" />
                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)">Allow PDF Export</span>
                                        </label>
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" checked={managingPlan.allowSharing ?? false} onChange={(e) => setManagingPlan({ ...managingPlan, allowSharing: e.target.checked })} className="w-5 h-5 accent-(--accent) cursor-pointer" />
                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)">Allow Sharing / Export</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 mt-8 pt-6 border-t border-(--border) shrink-0">
                                <button onClick={() => setManagingPlan(null)} className="flex-1 pill-button bg-transparent text-(--text-muted) border border-(--border) justify-center">Cancel</button>
                                <button onClick={handleSavePlan} disabled={actionLoading} className="flex-1 pill-button justify-center disabled:opacity-50">
                                    {actionLoading ? 'Saving...' : 'Save Plan'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Plan Confirmation */}
            {
                showPlanDeleteConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-in zoom-in-95 duration-300">
                        <div className="bento-card bg-white p-12 text-center max-w-2xl w-full relative overflow-hidden border-2 border-(--error)">
                            <div className="absolute top-0 left-0 w-full h-1 bg-(--error)"></div>
                            <span className="text-6xl block mb-8 animate-bounce">⚠️</span>
                            <h2 className="text-2xl font-black text-(--error) mb-6 uppercase tracking-[0.4em] ">Delete Plan</h2>
                            <p className="text-(--text-muted) text-[0.75rem] font-black uppercase tracking-widest leading-loose opacity-80 mb-10">
                                Are you sure you want to delete this subscription plan? Existing subscriptions in Stripe will not be affected, but this plan will no longer be visible or manageable here.
                                <br /><br />
                                <span className="text-(--text)">This action cannot be undone.</span>
                            </p>
                            <div className="flex gap-4">
                                <button onClick={() => setShowPlanDeleteConfirm(null)} className="flex-1 pill-button bg-white text-(--text) border-2 border-(--border) justify-center">Cancel</button>
                                <button onClick={() => handleDeletePlan(showPlanDeleteConfirm)} disabled={actionLoading} className="flex-1 pill-button bg-(--error) text-white border-none justify-center disabled:opacity-50">
                                    {actionLoading ? 'Deleting...' : 'Delete Plan'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
}
